import { useState, useEffect, useContext } from "react";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { fetchVendasSemTracking } from "../services/trackingServiceV2";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import JornadaModal from "../components/JornadaModal";

// Vendas do produto no período que o funil NÃO consegue explicar:
//   sem_visitor            → compra sem uid (cliente não passou pelo tracker, link sem xcod/sck)
//   visitor_fora_do_produto → tem uid, mas só com eventos fora das páginas deste produto
//                             (snippet genérico/antigo, ou veio de outra página). A jornada completa ainda existe.
// Período e produto chegam pela URL (?start&end&produto_id), vindos do Funil.

const MOTIVO = {
    sem_visitor: { label: "Sem visitante", cls: "bg-red-900/40 text-red-300", dica: "A compra chegou sem uid. O link do checkout não levou xcod/sck ou a pessoa não passou por página com tracker." },
    visitor_fora_do_produto: { label: "Fora do produto", cls: "bg-amber-900/40 text-amber-300", dica: "Tem uid, mas nenhuma página deste produto no período: snippet genérico/antigo ou outra página." },
    visitou_depois: { label: "Visitou depois", cls: "bg-blue-900/40 text-blue-300", dica: "Passou pelas páginas do produto só DEPOIS da compra (ex.: voltou pela página de obrigado ou por um link)." },
};

function formatR$(v) {
    return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

export default function VendasSemTracking() {
    const { isOpen } = useContext(SidebarContext);
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [jornadaVisitor, setJornadaVisitor] = useState(null);

    const start = sp.get("start") || "";
    const end = sp.get("end") || "";
    const produtoId = sp.get("produto_id") || "";

    useEffect(() => { loadData(); }, [start, end, produtoId]);

    async function loadData() {
        setLoading(true);
        try {
            setData(await fetchVendasSemTracking(start, end, produtoId));
        } catch {
            toast.error("Erro ao buscar vendas sem tracking");
        }
        setLoading(false);
    }

    return (
        <div className="min-h-screen bg-surface-1 text-white flex">
            <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full p-6 mb-16 sm:mb-0`}>
                <header className="mb-6">
                    <button onClick={() => navigate(-1)} className="text-ink-3 hover:text-white text-sm mb-3 inline-flex items-center gap-1">← Voltar</button>
                    <h1 className="text-3xl font-bold">Vendas Sem Tracking</h1>
                    <p className="text-ink-3 text-sm mt-1">
                        Vendas aprovadas do produto que o funil não consegue ligar a uma visita nas páginas dele.
                        {data?.periodo && <> Período <strong className="text-ink-2">{data.periodo.start}</strong> a <strong className="text-ink-2">{data.periodo.end}</strong>{data.produto_id ? <>, produto <strong className="text-ink-2">{data.produto_id}</strong></> : ", todos os produtos rastreados"}.</>}
                    </p>
                </header>

                {loading && <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>}

                {data && !loading && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3">
                            <div className="bg-surface-2 p-4 rounded-lg">
                                <span className="text-ink-3 text-sm">Total: </span>
                                <span className="text-amber-400 font-bold text-lg">{data.total}</span>
                                <span className="text-ink-4 text-xs ml-2">{formatR$(data.valor)}</span>
                            </div>
                            <div className="bg-surface-2 p-4 rounded-lg text-sm">
                                <span className={`${MOTIVO.sem_visitor.cls} px-2 py-0.5 rounded-full text-xs mr-2`}>Sem visitante</span><span className="text-ink-2 font-semibold">{data.motivos?.sem_visitor ?? 0}</span>
                                <span className={`${MOTIVO.visitor_fora_do_produto.cls} px-2 py-0.5 rounded-full text-xs ml-4 mr-2`}>Fora do produto</span><span className="text-ink-2 font-semibold">{data.motivos?.visitor_fora_do_produto ?? 0}</span>
                                <span className={`${MOTIVO.visitou_depois.cls} px-2 py-0.5 rounded-full text-xs ml-4 mr-2`}>Visitou depois</span><span className="text-ink-2 font-semibold">{data.motivos?.visitou_depois ?? 0}</span>
                            </div>
                        </div>

                        <div className="bg-surface-2 rounded-lg overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.08] text-ink-3">
                                        <th className="text-left p-3">Produto</th>
                                        <th className="text-left p-3">Cliente</th>
                                        <th className="text-left p-3">Email</th>
                                        <th className="text-left p-3">Valor</th>
                                        <th className="text-left p-3">Motivo</th>
                                        <th className="text-left p-3">Data</th>
                                        <th className="text-left p-3">Histórico</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.vendas && data.vendas.length > 0 ? data.vendas.map((v) => {
                                        const m = MOTIVO[v.motivo] || MOTIVO.sem_visitor;
                                        return (
                                            <tr key={v.id} className="border-b border-white/[0.08] hover:bg-surface-3/30">
                                                <td className="p-3 font-medium max-w-[200px] truncate">{v.nome_produto || "—"}</td>
                                                <td className="p-3">{v.nome || "—"}</td>
                                                <td className="p-3 text-ink-3 text-xs">{v.email || "—"}</td>
                                                <td className="p-3 text-emerald-400">{formatR$(v.valor_produtor)}</td>
                                                <td className="p-3"><span className={`${m.cls} px-2 py-0.5 rounded-full text-xs`} title={m.dica}>{m.label}</span></td>
                                                <td className="p-3 text-ink-3 text-xs whitespace-nowrap">{v.data_aprovacao ? new Date(v.data_aprovacao).toLocaleString("pt-BR") : "—"}</td>
                                                <td className="p-3">
                                                    {v.visitor_id ? <button onClick={() => setJornadaVisitor(v.visitor_id)} className="text-emerald-400 text-xs hover:underline">Ver jornada completa</button> : <span className="text-ink-5 text-xs">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr><td colSpan={7} className="text-center text-ink-4 p-6">🎉 Todas as vendas do período têm tracking!</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            {jornadaVisitor && <JornadaModal visitorId={jornadaVisitor} onClose={() => setJornadaVisitor(null)} />}
        </div>
    );
}
